import type { ReactNode } from 'react'
import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { useDashboardFeatureFlags } from '@components'
import { Dashboard } from '@components/common/Dashboard/Dashboard'
import { NavTabs } from '@components/common/NavTabs'
import { PageLayout } from '@components/common/PageLayout'
import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import { useWebviewEventsBus } from '@contexts/WebviewEventsBusContext'
import {
  Button,
  ButtonGroup,
  ToolbarButton,
  useMediaQueryContext,
  useModalContext,
} from '@fourthwall/components'
import { PlusIcon } from '@fourthwall/icons'
import { WebViewVisibilityDecorator } from '@fourthwall/webview-bridge'
import { routing } from '@utils/routing'

import {
  MiniRecommendations,
  MiniRecommendationsSkeleton,
  useMiniRecommendationsData,
} from '../../modules/MiniRecommendations'
import { CreatePromotionModal } from './modules/CreatePromotionModal'
import { GiftCardsFilter } from './pages/GiftCards'
import { PromotionCodesFilter } from './pages/PromotionCodes'

type PromotionsProps = {
  content: ReactNode
}

export const Promotions = ({ content }: PromotionsProps) => {
  const { open } = useModalContext()
  const media = useMediaQueryContext()
  const { hasPermission } = useDashboardPermissions()
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()
  const { isLoading, data: miniRecommendationsData } = useMiniRecommendationsData()
  const webviewEventBus = useWebviewEventsBus()

  const location = useLocation()
  const shouldRenderPromoCodesFilters =
    location.pathname === routing.promotions.promotionCodes.self &&
    hasPermission('promotions.shopPromoCodes')
  const shouldRenderGiftCardsFilters =
    location.pathname === routing.promotions.giftCards.self && hasPermission('promotions.giftCards')

  const handleCreatePromotionClick = () => open(CreatePromotionModal)

  useEffect(() => {
    const removeEventListener = webviewEventBus.addEventListener('openCreatePromotionModal', () => {
      handleCreatePromotionClick()
    })

    return removeEventListener
  }, [])

  const getToolbarContent = () => {
    if (!media.tablet) {
      return (
        <ButtonGroup space="medium">
          {shouldRenderPromoCodesFilters && <PromotionCodesFilter />}
          {shouldRenderGiftCardsFilters && <GiftCardsFilter />}
          <ToolbarButton
            label="Create promotion"
            icon={PlusIcon}
            onClick={handleCreatePromotionClick}
          />
        </ButtonGroup>
      )
    }

    return (
      <ButtonGroup space="medium">
        {shouldRenderPromoCodesFilters && <PromotionCodesFilter />}
        {shouldRenderGiftCardsFilters && <GiftCardsFilter />}
        <Button
          label="Create promotion"
          appearance="primary"
          size="small"
          onClick={handleCreatePromotionClick}
        />
      </ButtonGroup>
    )
  }

  const getNavbarTabs = () => {
    const tabs = []

    if (hasPermission('promotions.shopPromoCodes')) {
      tabs.push({
        label: 'Promo codes',
        to: routing.promotions.promotionCodes.self,
      })
    }

    if (hasPermission('promotions.giveawayLinks')) {
      tabs.push({
        label: 'Giveaway links',
        to: routing.promotions.giveawayPackages.self,
      })
    }

    if (hasPermission('promotions.giftCards') && dashboardFeatureFlags.giftCards.enabled) {
      tabs.push({
        label: 'Gift cards',
        to: routing.promotions.giftCards.self,
      })
    }

    return tabs
  }

  return (
    <PageLayout size="large">
      <Dashboard.Section
        name="Promotions"
        toolbarContent={getToolbarContent()}
        bottomContent={
          <WebViewVisibilityDecorator on="web">
            {isLoading && <MiniRecommendationsSkeleton />}

            {!isLoading && <MiniRecommendations data={miniRecommendationsData} page="promotions" />}
          </WebViewVisibilityDecorator>
        }
      >
        <Dashboard.Section.Item>
          <NavTabs tabs={getNavbarTabs()} />
        </Dashboard.Section.Item>
        {content}
      </Dashboard.Section>
    </PageLayout>
  )
}
